import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import Box from "components/Atoms/Box";
import BoxV1 from "components/Atoms/Box/BoxV1";
import Typography from "components/Atoms/Typography/TypographyV1";
import { selectTimeSelector } from "state/application/applicationSlice";
import { useGetEVChargingIntervalsForecastByCommunityIdPaginationQuery } from "state/evs/evApiSlice";
import { useGetResources } from "utils/crud/read";
import CarIcon from "./CarIcon";

const HOUR_LABELS = ["00", "03", "06", "09", "12", "15", "18", "21", "24"];
const TOTAL_MINUTES = 24 * 60;

function buildDaysInRange(start, end) {
  const days = [];
  const d = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  while (d <= last) {
    days.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return days;
}

function SwimlaneChart({ communityId }) {
  const { t, i18n } = useTranslation(["internalLoads"]);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const { startDate, endDate } = useSelector(selectTimeSelector);
  const isMultiDay = startDate && endDate && startDate !== endDate;

  // detailDay: which day to drill into; null = heatmap overview (multi-day) or use startDate (single)
  const [detailDay, setDetailDay] = useState(null);

  useEffect(() => {
    setDetailDay(null);
  }, [startDate, endDate]);

  const activeDay = isMultiDay ? detailDay : startDate ?? null;
  const showHeatmap = isMultiDay && !detailDay;
  const showSwimlane = !isMultiDay || !!detailDay;

  const resources = useGetResources("true");

  // Overview fetch: range query (no date filter) — used to populate heatmap
  const { data: overviewData, isFetching: isFetchingOverview } =
    useGetEVChargingIntervalsForecastByCommunityIdPaginationQuery(
      { page: 0, limit: 50000, startDate, endDate, communityId },
      { skip: !showHeatmap || !startDate }
    );

  // Detail fetch: single day — used for swimlane
  const { data: detailData, isFetching: isFetchingDetail } =
    useGetEVChargingIntervalsForecastByCommunityIdPaginationQuery(
      { page: 0, limit: 10000, date: activeDay, communityId },
      { skip: !showSwimlane || !activeDay }
    );

  // Heatmap: which days have ≥1 connected record
  const daysInRange = useMemo(
    () => (isMultiDay && startDate && endDate ? buildDaysInRange(startDate, endDate) : []),
    [isMultiDay, startDate, endDate]
  );

  const daysWithData = useMemo(() => {
    if (!overviewData?.data?.length) return new Set();
    const s = new Set();
    overviewData.data.forEach((row) => {
      if (row.is_connected) s.add(row.datetime_timestamp.slice(0, 10));
    });
    return s;
  }, [overviewData]);

  // Group daysInRange by year-month for heatmap section headers
  const monthGroups = useMemo(() => {
    const groups = [];
    let current = null;
    daysInRange.forEach((day) => {
      const ym = day.slice(0, 7); // "YYYY-MM"
      if (!current || current.ym !== ym) {
        const d = new Date(`${day}T00:00:00Z`);
        current = { ym, year: d.getUTCFullYear(), month: d.getUTCMonth(), days: [] };
        groups.push(current);
      }
      current.days.push(day);
    });
    return groups;
  }, [daysInRange]);

  // Only show months with ≥1 day of forecast data; track how many are hidden
  const visibleMonthGroups = useMemo(
    () => monthGroups.filter((g) => g.days.some((d) => daysWithData.has(d))),
    [monthGroups, daysWithData]
  );
  const hiddenMonthCount = monthGroups.length - visibleMonthGroups.length;

  // Swimlane rows: group records by ev_id → Set<minuteOfDay>
  const evRows = useMemo(() => {
    if (!detailData?.data?.length) return [];
    const map = {};
    detailData.data.forEach((row) => {
      if (!map[row.ev_id]) map[row.ev_id] = { evId: row.ev_id, connected: new Set() };
      if (row.is_connected) {
        const d = new Date(row.datetime_timestamp);
        map[row.ev_id].connected.add(d.getHours() * 60 + d.getMinutes());
      }
    });
    return Object.values(map);
  }, [detailData]);

  const getEvLabel = (evId) =>
    resources?.find((r) => r.id === evId)?.name ?? String(evId).slice(0, 8);

  const fmtMinute = (m) =>
    `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

  const getTransitions = (connected) => {
    const labels = [];
    connected.forEach((m) => {
      if (!connected.has(m - 15)) labels.push({ minute: m, label: fmtMinute(m) });
      if (!connected.has(m + 15)) labels.push({ minute: m + 15, label: fmtMinute(m + 15) });
    });
    return labels;
  };

  const connectedColor = "#00bfa5";
  const disconnectedBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)";
  const gridColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const labelColor = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.4)";

  return (
    <div className="rec-card">
      <Box p={3}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3} gap={1}>
          {isMultiDay && detailDay && (
            <IconButton size="small" onClick={() => setDetailDay(null)} sx={{ p: 0.5, mr: 0.5 }}>
              <Icon sx={{ fontSize: "1.2rem !important" }}>arrow_back</Icon>
            </IconButton>
          )}
          <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
            {t("pages.ev_charging_intervals_forecast.title")}
          </Typography>
          {activeDay && (
            <Typography
              variant="caption"
              sx={{ color: labelColor, fontVariantNumeric: "tabular-nums" }}
            >
              {isMultiDay ? `${startDate} → ${endDate}` : activeDay}
            </Typography>
          )}
        </Box>

        {/* ── HEATMAP VIEW ────────────────────────────────── */}
        {showHeatmap && (
          <>
            {isFetchingOverview && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {/* eslint-disable react/no-array-index-key */}
                {Array.from({ length: 30 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    width={44}
                    height={52}
                    sx={{ borderRadius: "8px" }}
                  />
                ))}
                {/* eslint-enable react/no-array-index-key */}
              </Box>
            )}

            {!isFetchingOverview && visibleMonthGroups.length > 0 && (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {visibleMonthGroups.map(({ ym, year, month, days }) => (
                    <Box key={ym}>
                      {/* Month + year label */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.75,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: labelColor,
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                          }}
                        >
                          {new Intl.DateTimeFormat(i18n.language, { month: "long" }).format(
                            new Date(year, month)
                          )}{" "}
                          {year}
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            height: "1px",
                            backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                          }}
                        />
                      </Box>

                      {/* Day cells */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {days.map((day) => {
                          const d = new Date(`${day}T00:00:00Z`);
                          const hasData = daysWithData.has(day);

                          let borderColor;
                          if (hasData) borderColor = `${connectedColor}66`;
                          else if (isDark) borderColor = "rgba(255,255,255,0.08)";
                          else borderColor = "rgba(0,0,0,0.08)";

                          let bgColor;
                          if (hasData)
                            bgColor = isDark ? "rgba(0,191,165,0.18)" : "rgba(0,191,165,0.1)";
                          else bgColor = disconnectedBg;

                          let bgHover;
                          if (hasData)
                            bgHover = isDark ? "rgba(0,191,165,0.3)" : "rgba(0,191,165,0.2)";
                          else if (isDark) bgHover = "rgba(255,255,255,0.1)";
                          else bgHover = "rgba(0,0,0,0.1)";

                          let dayNumColor;
                          if (hasData) dayNumColor = connectedColor;
                          else if (isDark) dayNumColor = "rgba(255,255,255,0.35)";
                          else dayNumColor = "rgba(0,0,0,0.3)";

                          return (
                            <Tooltip key={day} title={day} arrow placement="top">
                              <Box
                                onClick={() => setDetailDay(day)}
                                sx={{
                                  width: 44,
                                  height: 48,
                                  borderRadius: "8px",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  border: `1px solid ${borderColor}`,
                                  backgroundColor: bgColor,
                                  transition: "background-color 0.15s, border-color 0.15s",
                                  "&:hover": { backgroundColor: bgHover },
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: "1rem",
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    color: dayNumColor,
                                  }}
                                >
                                  {d.getUTCDate()}
                                </Box>
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: "0.6rem",
                                    color: labelColor,
                                    lineHeight: 1,
                                    mt: 0.4,
                                  }}
                                >
                                  {new Intl.DateTimeFormat(i18n.language, {
                                    weekday: "short",
                                  }).format(d)}
                                </Box>
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Heatmap legend + hidden months note */}
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                  {hiddenMonthCount > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ color: labelColor, opacity: 0.7, fontStyle: "italic", mr: "auto" }}
                    >
                      {t("pages.ev_charging_intervals_forecast.heatmap_months_hidden_other", {
                        count: hiddenMonthCount,
                        defaultValue: "{{count}} meses sem previsão ocultados",
                      })}
                    </Typography>
                  )}
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.75}
                    sx={{ ml: hiddenMonthCount > 0 ? 0 : "auto" }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "3px",
                        backgroundColor: isDark ? "rgba(0,191,165,0.18)" : "rgba(0,191,165,0.1)",
                        border: `1px solid ${connectedColor}66`,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: labelColor }}>
                      {t("pages.ev_charging_intervals_forecast.heatmap_with_forecast", {
                        defaultValue: "Com previsão",
                      })}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "3px",
                        backgroundColor: disconnectedBg,
                        border: `1px solid ${gridColor}`,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: labelColor }}>
                      {t("pages.ev_charging_intervals_forecast.heatmap_without_forecast", {
                        defaultValue: "Sem previsão",
                      })}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </>
        )}

        {/* ── SWIMLANE VIEW ───────────────────────────────── */}
        {showSwimlane && (
          <>
            {/* Loading skeleton */}
            {activeDay && isFetchingDetail && (
              <Box display="flex" flexDirection="column" gap={1.5}>
                {[1, 2, 3].map((i) => (
                  <Box key={i} display="flex" alignItems="center" gap={2}>
                    <Skeleton
                      variant="rectangular"
                      width={48}
                      height={24}
                      sx={{ borderRadius: 1 }}
                    />
                    <Skeleton variant="text" width={60} />
                    <Skeleton variant="rectangular" height={28} sx={{ flex: 1, borderRadius: 1 }} />
                  </Box>
                ))}
              </Box>
            )}

            {/* Empty state */}
            {activeDay && !isFetchingDetail && evRows.length === 0 && (
              <BoxV1
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  borderRadius: "10px",
                  border: `1px dashed ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)"}`,
                }}
              >
                <Icon sx={{ fontSize: "2.5rem !important", color: labelColor, mb: 1 }}>
                  ev_station
                </Icon>
                <Typography variant="body2" sx={{ color: labelColor }}>
                  {t("pages.ev_charging_intervals_forecast.no_data_hint")}
                </Typography>
              </BoxV1>
            )}

            {/* Swimlane */}
            {activeDay && !isFetchingDetail && evRows.length > 0 && (
              <Box>
                {/* Day label when drilling in from heatmap */}
                {isMultiDay && detailDay && (
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: connectedColor, mb: 1.5 }}
                  >
                    {detailDay}
                  </Typography>
                )}

                {/* Time axis header */}
                <Box display="flex" ml="172px" mb={0.5}>
                  {HOUR_LABELS.map((label) => (
                    <BoxV1
                      key={label}
                      sx={{
                        flex: label === "24" ? 0 : 3,
                        textAlign: "left",
                        fontSize: "0.7rem",
                        color: labelColor,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {label}h
                    </BoxV1>
                  ))}
                </Box>

                {/* EV rows */}
                <Box display="flex" flexDirection="column" gap={1}>
                  {evRows.map(({ evId, connected }) => (
                    <Box key={evId} display="flex" alignItems="center" gap={1.5}>
                      {/* Car icon */}
                      <BoxV1
                        sx={{ width: 48, flexShrink: 0, display: "flex", justifyContent: "center" }}
                      >
                        <CarIcon
                          color={connected.size > 0 ? connectedColor : labelColor}
                          size={20}
                        />
                      </BoxV1>
                      {/* EV label */}
                      <BoxV1
                        sx={{
                          width: 110,
                          flexShrink: 0,
                          fontSize: "0.78rem",
                          fontWeight: 500,
                          color: isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.75)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getEvLabel(evId)}
                      </BoxV1>
                      {/* Timeline bar + transition labels */}
                      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <Box
                          sx={{
                            height: 28,
                            position: "relative",
                            borderRadius: "6px",
                            overflow: "hidden",
                            backgroundColor: disconnectedBg,
                          }}
                        >
                          {/* Grid lines */}
                          {[3, 6, 9, 12, 15, 18, 21].map((h) => (
                            <Box
                              key={h}
                              sx={{
                                position: "absolute",
                                top: 0,
                                bottom: 0,
                                left: `${(h * 60 * 100) / TOTAL_MINUTES}%`,
                                width: "1px",
                                backgroundColor: gridColor,
                                zIndex: 1,
                              }}
                            />
                          ))}
                          {/* Connected segments */}
                          {Array.from(connected).map((minuteOfDay) => (
                            <Box
                              key={minuteOfDay}
                              sx={{
                                position: "absolute",
                                top: 0,
                                bottom: 0,
                                left: `${(minuteOfDay * 100) / TOTAL_MINUTES}%`,
                                width: `${(15 * 100) / TOTAL_MINUTES}%`,
                                backgroundColor: connectedColor,
                                opacity: 0.85,
                                zIndex: 2,
                              }}
                            />
                          ))}
                        </Box>
                        {/* Transition time labels */}
                        <Box sx={{ height: 13, position: "relative" }}>
                          {getTransitions(connected).map(({ minute, label }) => (
                            <Box
                              key={minute}
                              sx={{
                                position: "absolute",
                                top: 1,
                                left: `${(minute * 100) / TOTAL_MINUTES}%`,
                                transform: "translateX(-50%)",
                                fontSize: "0.6rem",
                                lineHeight: 1,
                                color: labelColor,
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                                userSelect: "none",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {label}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Legend */}
                <Box display="flex" alignItems="center" gap={2} mt={2} justifyContent="flex-end">
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "3px",
                        backgroundColor: connectedColor,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: labelColor }}>
                      {t("pages.ev_charging_intervals_forecast.legend_connected")}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "3px",
                        backgroundColor: disconnectedBg,
                        border: `1px solid ${gridColor}`,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: labelColor }}>
                      {t("pages.ev_charging_intervals_forecast.legend_disconnected")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </div>
  );
}

SwimlaneChart.propTypes = {
  communityId: PropTypes.string.isRequired,
};

export default SwimlaneChart;
